import { gsap, ScrollTrigger } from "@/composeables/gsap"

// Bühne-Variante der Ärzte-Section (sectionPractitionersVariant = stage).
//
// - Das Hintergrundbild fadet beim Reinscrollen ein (analog zur reveal-Variante
//   von section-text-on-image: autoAlpha 0 → 1, gescrubbt).
// - Die Section pinnt vertikal zentriert (min-height 50vh, start "center center").
// - Solange gepinnt, scrollt die Karten-Spur von unten nach oben durch; die
//   Karten verschwinden hinter dem Farbverlauf an der Oberkante (CSS z-index).
//
// Die Maße (Sichtfenster- und Grid-Höhe) werden als Funktions-Werte übergeben,
// damit invalidateOnRefresh sie bei Resize/Reflow (Bild-Load) neu auswertet.
function createStage(section) {
    const stage = section.querySelector(".section-practitioners__stage")
    // Hintergrundbild = Basis-`.section__bg` (aus den Block-Settings gerendert).
    const bg = section.querySelector(".section__bg")
    const track = section.querySelector(".section-practitioners__stage-track")
    const grid = section.querySelector(".section-practitioners__stage-grid")
    const header = section.querySelector(".section-practitioners__stage-header")

    if (!stage || !track || !grid) return

    // Hintergrundbild einfaden, während die Section von unten reinscrollt —
    // voll sichtbar genau bei "center center", also wenn der Pin startet und
    // die Headline zu scrollen beginnt (gleicher Punkt wie der Pin-Start).
    if (bg) {
        // Direkt das <img> faden (nicht den Wrapper) und OHNE will-change:
        // Firefox promotet einen will-change:opacity-Wrapper auf eine
        // Compositor-Ebene, deren Opacity beim gescrubbten Scrollen nicht neu
        // gemalt wird — der DOM-Wert animiert 0→1, das ersetzte <img> bleibt
        // aber voll sichtbar. Opacity direkt aufs <img> ohne Layer-Promotion
        // malt jeden Frame korrekt.
        const bgImg = bg.querySelector(".media__media-element, img") || bg
        gsap.set(bgImg, { autoAlpha: 0 })
        gsap.to(bgImg, {
            autoAlpha: 1,
            ease: "none",
            scrollTrigger: {
                trigger: section,
                start: "top bottom",
                end: "center center",
                scrub: true,
                invalidateOnRefresh: true,
            },
        })

        // Abdunkel-Overlay (CSS ::after auf .section__bg): --stage-bg-dim von 0 → 1
        // gescrubbt über DIESELBE Scroll-Range wie der Bild-Fade, sodass das Bild
        // beim Reinscrollen abdunkelt und der Text bei "center center" (Pin-Start)
        // gut lesbar auf dem dann voll abgedunkelten Bild steht.
        gsap.set(bg, { "--stage-bg-dim": 0 })
        gsap.to(bg, {
            "--stage-bg-dim": 1,
            ease: "none",
            scrollTrigger: {
                trigger: section,
                start: "top bottom",
                end: "center center",
                scrub: true,
                invalidateOnRefresh: true,
            },
        })

        // Leichter Zoom: das Bild skaliert beim Durchscrollen der Section sanft
        // von 1 → 1.12 (gescrubbt, ganze Sichtbarkeitsdauer top-bottom → bottom-top).
        // object-fit:cover + overflow:clip (.section__bg) beschneiden den Überstand
        // sauber, transform-origin center = Zoom aus der Bildmitte. force3D für
        // GPU-Compositing (flüssiges Scrubben).
        gsap.fromTo(
            bgImg,
            { scale: 1 },
            {
                scale: 1.12,
                ease: "none",
                force3D: true,
                scrollTrigger: {
                    trigger: section,
                    start: "top bottom",
                    end: "bottom top",
                    scrub: true,
                    invalidateOnRefresh: true,
                },
            }
        )
    }

    // Nachbar-Sections JETZT merken – vor dem Pin. Der Pin (pin:true) wickelt die
    // Bühne in einen .pin-spacer; danach wären previous/nextElementSibling null.
    const prevSection = section.previousElementSibling
    const nextSection = section.nextElementSibling

    // Pin + Durchlauf. matchMedia, damit ein Breakpoint-/Resize-Wechsel sauber
    // neu aufsetzt (Grid-Spaltenzahl ändert die Grid-Höhe).
    // Sichtfenster-Höhe = 1,25 × Höhe einer Karte (auf --stage-window-height,
    // fällt per CSS auf 50vh zurück, falls JS nicht läuft). Bei jedem Refresh
    // neu messen (Breakpoint ändert Spaltenzahl → Kartenhöhe), vor der Positions-
    // berechnung der ScrollTrigger.
    const setStageHeight = () => {
        const card = section.querySelector(".section-practitioners__item")
        const cardH = card ? card.getBoundingClientRect().height : 0
        if (!cardH) return

        // Konfiguriertes Section-Padding (oben+unten). In der Bühne ist das
        // angewandte padding genullt, die --padding-behavior-*-Variable trägt aber
        // weiterhin den konfigurierten Wert (aus den Block-Settings). getComputedStyle
        // löst die --dimension-* zu px auf.
        const cs = getComputedStyle(section)
        const px = (v) => {
            const n = parseFloat(v)
            return Number.isFinite(n) ? n : 0
        }
        const padTop = px(cs.getPropertyValue("--padding-behavior-top"))
        const padBottom = px(cs.getPropertyValue("--padding-behavior-bottom"))

        // Sichtfenster = max(minVh, Kachelhöhe + Padding oben+unten). So ist die
        // Bühne nie flacher als die vh-Untergrenze und fasst immer die volle Karte
        // samt der konfigurierten Section-Abstände. Die Untergrenze kommt als
        // Fraktion aus --stage-min-vh (SCSS: 0.6, ab hd/1920px 0.7) – so bleibt der
        // Breakpoint im SCSS bei den anderen Breakpoints, keine Magic Number im JS.
        const minVhFraction = parseFloat(cs.getPropertyValue("--stage-min-vh")) || 0.6
        const minVh = window.innerHeight * minVhFraction
        let windowH = Math.max(minVh, cardH + padTop + padBottom)

        // Mobil ist der Header unten verankert (yPercent 0) und wächst bei langem
        // Text nach oben – ohne Grenze bis in die Kachel-Spur hinein. Reicht die
        // Fensterhöhe nicht für Header (samt seiner 100px-Bodenmarge) + 250px
        // Schutzzone zur Spur, wird das Fenster (und damit die Scrollstrecke)
        // entsprechend vergrößert, statt den Text abzuschneiden.
        if (header) {
            const isBottomAnchored = parseFloat(getComputedStyle(header).getPropertyValue("--stage-header-ypercent")) === 0
            if (isBottomAnchored) {
                const headerH = header.getBoundingClientRect().height
                const headerBottomOffset = px(getComputedStyle(header).insetBlockEnd)
                const requiredH = headerH + headerBottomOffset + 250
                windowH = Math.max(windowH, requiredH)
            }
        }

        stage.style.setProperty("--stage-window-height", windowH + "px")
        // Verlaufshöhe (oben wie unten): der Freiraum über/unter einer vertikal
        // mittig stehenden Karte = (windowH − cardH) / 2, minus 10px Schutzzone,
        // damit die zentrierte Kartenreihe garantiert nicht angefadet wird.
        const gradientH = Math.max(0, (windowH - cardH) / 2 - 10)
        stage.style.setProperty("--stage-gradient-height", gradientH + "px")

        // Vertikalen Header-Anker (yPercent) breakpoint-abhängig aus dem SCSS lesen:
        // mobil 0 (unten verankert), ab sm -50 (um die eigene Mitte auf top:50%).
        // Bei jedem Refresh neu, damit ein Breakpoint-Wechsel greift. Der Parallax
        // nutzt y (additiv) und bleibt davon unberührt.
        if (header) {
            const yp = parseFloat(getComputedStyle(header).getPropertyValue("--stage-header-ypercent"))
            gsap.set(header, { yPercent: Number.isFinite(yp) ? yp : -50 })
        }
    }
    ScrollTrigger.addEventListener("refreshInit", setStageHeight)
    setStageHeight()

    // Karten initial unsichtbar – sie faden gestaffelt ein, während sie beim
    // Durchscrollen von unten hochkommen (an die Pin-Timeline gekoppelt, siehe
    // unten). Ein einfacher Eintritts-Reveal verpufft hier, weil die Karten beim
    // Section-Eintritt noch weit unterhalb des Sichtfensters im Track sitzen.
    const cards = gsap.utils.toArray(grid.querySelectorAll(".section-practitioners__item"))
    if (cards.length) gsap.set(cards, { autoAlpha: 0 })

    const mm = gsap.matchMedia()
    mm.add("(min-width: 0px)", () => {
        gsap.set(track, { willChange: "transform" })

        const windowH = () => stage.clientHeight

        // Abstand von der Grid-Oberkante zur Mitte der LETZTEN Kartenreihe
        // (transform-invariant: Differenz zweier Bounding-Rects im selben Track).
        const lastRowCenter = () => {
            const items = grid.querySelectorAll(".section-practitioners__item")
            if (!items.length) return grid.offsetHeight
            const last = items[items.length - 1]
            const gr = grid.getBoundingClientRect()
            const lr = last.getBoundingClientRect()
            return lr.top - gr.top + lr.height / 2
        }

        // Start: erste Reihe steigt von unten rein (Grid-Oberkante an der
        // Fenster-Unterkante). Ende: die LETZTE Reihe steht vertikal mittig
        // (statt komplett rauszuscrollen). travel = tatsächlicher Karten-Weg,
        // damit das Karten-Tempo ~1:1 bleibt und Pin-Länge/Nachbarn synchron sind.
        const trackStartY = () => windowH()
        const trackEndY = () => windowH() / 2 - lastRowCenter()
        const travel = () => trackStartY() - trackEndY()

        const timeline = gsap.timeline().fromTo(
            track,
            { y: () => trackStartY() },
            { y: () => trackEndY(), ease: "none" },
            0
        )

        // Die Section danach direkt an die Bühnen-Unterkante andocken (statt sie
        // erst am Pin-Ende auftauchen zu lassen). Der Bühne-Pin-Spacer schiebt sie
        // um `travel` nach unten; ein gescrubbter Transform hebt sie exakt um diesen
        // Versatz wieder hoch → netto bleibt sie konstant angedockt und läuft am
        // Pin-Ende (y:0) nahtlos in den normalen Fluss über.
        // WICHTIG: als Teil DIESER Timeline (an den Pin-Trigger gebunden), NICHT als
        // eigener ScrollTrigger. Sonst laufen beim Resize zwei separate Trigger auf
        // derselben Range asynchron neu auf → die nächste Section hängt kurz am alten
        // (falschen) y-Offset und überlappt die gepinnte Stage, bis der nächste
        // Scrub-/Resize-Tick sie korrigiert. In der gemeinsamen Timeline wird der
        // Wert immer synchron mit dem Pin invalidiert.
        if (nextSection) {
            timeline.fromTo(
                nextSection,
                { y: () => -travel() },
                { y: 0, ease: "none" },
                0
            )
        }

        // Gestaffeltes Erscheinen POSITIONSBASIERT: jede Karte scheint durch, wenn
        // sie unten in die Bühne eintritt, und wird opaque, sobald sie vollständig
        // im Sichtfenster steht. Nicht an den Timeline-Fortschritt gekoppelt (das
        // ließ die Karten dauerhaft halbtransparent), sondern pro Frame aus der
        // tatsächlichen Y-Position der Karte im Stage berechnet. Der Stagger
        // entsteht von selbst, weil die Reihen zeitversetzt eintreten.
        const FADE_ZONE = 0.28 // unteres Fenster-Drittel: hier fadet die Karte ein
        const updateCardOpacity = () => {
            const stageH = stage.clientHeight
            if (!stageH) return
            const fadeStart = stageH * (1 - FADE_ZONE) // ab hier (weiter unten) durchsichtig
            for (const card of cards) {
                const r = card.getBoundingClientRect()
                const sr = stage.getBoundingClientRect()
                const cardTopInStage = r.top - sr.top
                // Karte voll opaque, sobald ihre Oberkante über fadeStart steht;
                // linear durchscheinend, während sie durch die untere Fade-Zone läuft;
                // ganz unten/außerhalb = 0.
                let o = (stageH - cardTopInStage) / (stageH - fadeStart)
                o = Math.max(0, Math.min(1, o))
                card.style.opacity = o
                card.style.visibility = o > 0 ? "visible" : "hidden"
            }
        }

        // Header-Parallax + Ausfaden. Der vertikale Start-Anker (yPercent) wird in
        // setStageHeight breakpoint-abhängig gesetzt (mobil 0 = unten verankert, ab
        // sm -50 = zentriert) — hier NICHT erneut setzen, sonst überschriebe es den
        // Mobile-Wert. Beim Scrollen driftet die Headline mit 0,5× nach oben (Parallax)
        // und fadet mit 1× aus (opacity 1→0 über eine Fensterhöhe Scroll).
        let headerFade
        if (header) {
            // Bewegung 0,5×: über die Pin-Range (travel) genau 0,5·travel hoch.
            timeline.fromTo(
                header,
                { y: 0 },
                { y: () => -0.5 * travel(), ease: "none" },
                0
            )
            // Ausfaden 1×: opacity 1→0 über eine Sichtfenster-Höhe Scroll.
            headerFade = gsap.fromTo(
                header,
                { autoAlpha: 1 },
                {
                    autoAlpha: 0,
                    ease: "none",
                    scrollTrigger: {
                        trigger: section,
                        start: "center center",
                        end: () => "+=" + windowH(),
                        scrub: true,
                        invalidateOnRefresh: true,
                    },
                }
            )
        }

        const st = ScrollTrigger.create({
            trigger: section,
            start: "center center",
            end: () => "+=" + travel(),
            pin: true,
            scrub: true,
            anticipatePin: 1,
            animation: timeline,
            invalidateOnRefresh: true,
            // Mobile: nicht bei jeder Viewport-Höhenänderung durch die ein-/
            // ausblendende Adressleiste neu refreshen – das lässt den Pin sonst
            // ständig springen. Nur echte Breiten-Resizes lösen ein Refresh aus.
            ignoreMobileResize: true,
            // Karten-Opacity positionsbasiert pro Frame nachziehen (durchscheinend
            // beim Eintritt unten → opaque im Sichtfenster). onRefresh, damit der
            // Zustand nach Resize/Reflow sofort stimmt (nicht erst beim nächsten Scroll).
            onUpdate: updateCardOpacity,
            onRefresh: updateCardOpacity,
        })
        updateCardOpacity()

        // Die Section davor an ihrer aktuellen Position einfrieren, solange die
        // Bühne gepinnt ist (gleiche Scroll-Range) — statt sie wegscrollen zu
        // lassen. pinSpacing:false: kein zusätzlicher Platz, die Bühne-Range
        // trägt den Scrollweg; die vorherige Section bleibt einfach fixiert.
        let prevSt
        if (prevSection) {
            prevSt = ScrollTrigger.create({
                trigger: section,
                start: "center center",
                end: () => "+=" + travel(),
                pin: prevSection,
                pinSpacing: false,
                anticipatePin: 1,
                invalidateOnRefresh: true,
                ignoreMobileResize: true,
            })
        }

        return () => {
            ScrollTrigger.removeEventListener("refreshInit", setStageHeight)
            // st.kill() killt auch die Timeline inkl. des eingebetteten
            // nextSection-Tweens; dessen Transform danach zurücksetzen.
            st.kill()
            if (header) gsap.set(header, { clearProps: "transform" })
            if (prevSt) prevSt.kill()
            if (nextSection) gsap.set(nextSection, { clearProps: "transform" })
            // Inline-Opacity/-Visibility der Karten zurücksetzen (matchMedia-Teardown
            // bei Breakpoint-Wechsel), damit der positionsbasierte Fade sauber neu aufsetzt.
            for (const card of cards) {
                card.style.removeProperty("opacity")
                card.style.removeProperty("visibility")
            }
        }
    })
}

export const init = () => {
    const stages = gsap.utils.toArray(".section-practitioners--stage")
    if (!stages.length) return

    stages.forEach(createStage)

    // Robustes Refresh nach echtem (Breiten-)Resize: bei diesem verschachtelten
    // Multi-Pin-Setup (Haupt-Section + prev-Pin + next-Andocken) müssen die
    // Pin-Positionen nach einem Fenster-Resize/Breakpoint-Wechsel neu berechnet
    // werden. Debounced (Resize-Ende abwarten) und NUR bei Breitenänderung –
    // reine Höhenänderungen (Mobile-Adressleiste) ignorieren wir bewusst
    // (deckt sich mit ignoreMobileResize auf den Pins).
    let lastWidth = window.innerWidth
    let resizeTimer
    window.addEventListener("resize", () => {
        if (window.innerWidth === lastWidth) return
        lastWidth = window.innerWidth
        clearTimeout(resizeTimer)
        resizeTimer = setTimeout(() => ScrollTrigger.refresh(), 200)
    })
}

export default init
